import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Facebook } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import api from "@/api";
import { useAuth } from "@/auth";
import { useLang, imgUrl } from "@/i18n";

export default function News() {
  const { t } = useLang();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", content: "", image_path: "" });
  const isStaff = ["admin", "staff"].includes(user?.role);

  const load = async () => {
    const r = await api.get("/news");
    setItems(r.data);
  };
  useEffect(() => { load().catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/news", form);
      toast.success(t("saved"));
      if (r.data.fb_posted) toast.success(t("fbPosted"));
      setOpen(false);
      setForm({ title: "", content: "", image_path: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error"));
    }
  };

  return (
    <div className="space-y-6" data-testid="news-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-['Outfit'] text-2xl font-bold text-[#165A2E] md:text-3xl">{t("news")}</h1>
        {isStaff && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-news-btn" className="rounded-full bg-[#84CC16] text-[#0A0A0A] hover:bg-[#65A30D]">
                <Plus size={16} className="mr-1" /> {t("addNews")}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{t("addNews")}</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label>{t("title")} *</Label>
                  <Input data-testid="news-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label>{t("content")} *</Label>
                  <Textarea data-testid="news-content" required rows={4} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label>{t("image")}</Label>
                  <Input data-testid="news-image" type="file" accept="image/*" className="mt-1"
                    onChange={async (e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      const fd = new FormData();
                      fd.append("file", f);
                      try {
                        const r = await api.post("/upload", fd);
                        setForm((p) => ({ ...p, image_path: r.data.path }));
                        toast.success(t("saved"));
                      } catch { toast.error(t("error")); }
                    }} />
                </div>
                <Button data-testid="news-submit-btn" className="w-full rounded-full bg-[#165A2E] text-white hover:bg-[#114724]">{t("publish")}</Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
        {items.map((n, i) => (
          <article key={n.news_id} className={`gfa-card gfa-card-hover animate-fade-up stagger-${Math.min(i + 1, 4)} overflow-hidden`} data-testid={`news-card-${n.news_id}`}>
            {n.image_path && <img src={imgUrl(n.image_path)} alt="" className="h-44 w-full object-cover" />}
            <div className="p-5">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-['Outfit'] text-lg font-bold text-slate-800">{n.title}</h3>
                {user?.role === "admin" && (
                  <Button size="icon" variant="ghost" className="text-red-500" data-testid={`delete-news-${n.news_id}`}
                    onClick={async () => { if (window.confirm(t("confirmDelete"))) { await api.delete(`/news/${n.news_id}`); load(); } }}>
                    <Trash2 size={15} />
                  </Button>
                )}
              </div>
              <p className="mt-2 line-clamp-4 text-sm text-slate-600">{n.content}</p>
              <div className="mt-4 flex items-center justify-between text-xs text-slate-400">
                <span>{n.created_by} · {new Date(n.created_at).toLocaleDateString()}</span>
                {n.fb_posted && (
                  <span className="flex items-center gap-1 font-semibold text-blue-600" data-testid={`fb-badge-${n.news_id}`}>
                    <Facebook size={12} /> {t("fbPosted")}
                  </span>
                )}
              </div>
            </div>
          </article>
        ))}
        {items.length === 0 && <p className="text-slate-400">{t("noData")}</p>}
      </div>
    </div>
  );
}
